import { ChangeEvent, useState } from 'react';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import { deleteImage, uploadImage } from '../../functions/API';
import { addElement } from '../../functions/launcherElementFunctions/addElement';
import { updateObjectNestedProperties } from '../../functions/objectFunctions/updateObjectNestedProperties';
import { useElements, useScene } from '../../hooks';
import { useEditor } from '../../hooks/useEditor';
import { ImgData } from '../../types/hooks';
import { LauncherElement } from '../../types/launcherElement';
import { Searchbar } from '../common/Searchbar';
import { TextButton } from '../generic/TextButton';
import { Dialog } from '../generic/dialog/Dialog';

export const Assets = () => {
  const { launcherThemeSession, setLauncherThemeSession, userSession } =
    useEditor();
  const scene = useScene();
  const lastSelectedElementID = scene.selectedElementIDs.slice(-1)[0];
  const [selectedElement, setSelectedElement] = useElements<LauncherElement>({
    id: lastSelectedElementID,
  });
  const [elements, setElements] = useElements<LauncherElement[]>();
  const [searchQuery, setSearchQuery] = useState('');
  const [isDragOver, setDragOver] = useState(false);

  const images = launcherThemeSession.images || [];

  const [fileInputText, setFileInputText] = useState(
    'Drop your file here or browse'
  );

  const onFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    if (!launcherThemeSession.themeID) return;

    const imgData = e.target.files?.[0];
    if (!imgData) {
      setFileInputText('Drop your file here or browse');
      return;
    }
    setFileInputText(imgData.name);

    // Upload img
    const uploadedImg = await toast.promise(
      uploadImage({
        themeID: launcherThemeSession.themeID,
        imageData: imgData,
      }),
      {
        pending: 'Uploading image',
        success: 'Image has been uploaded',
        error: 'Error while uploading the image',
      }
    );

    if (uploadedImg) {
      setLauncherThemeSession((themeData) => ({
        ...themeData,
        images: [uploadedImg, ...(themeData?.images || [])],
      }));
    }

    e.target.value = '';
    setFileInputText('Drop your file here or browse');
  };

  const onImgClick = (src: string) => {
    if (scene.selectedElementIDs.length <= 1) {
      // Update img src for selected element
      if (selectedElement?.type === 'img') {
        setSelectedElement((prev) =>
          updateObjectNestedProperties(prev, {
            'props.src': src,
          })
        );
        return;
      }

      // Add new img element
      const [newElements, newElement] = addElement({
        elements,
        elementType: 'img',
        targetId: lastSelectedElementID,
      });
      if (!newElement) return;

      newElement.props.src = src;

      setElements(newElements);
      scene.setSelectedElementIDs([newElement.id]);
    } else if (scene.selectedElementIDs.length > 1) {
      // TODO: Multiselect
    }
  };

  const handleDeleteImage = async (img: ImgData) => {
    if (!launcherThemeSession.themeID) return;

    await toast.promise(
      deleteImage({ themeID: launcherThemeSession.themeID, imgID: img.id }),
      {
        pending: 'Deleting image',
        success: 'Image has been deleted',
        error: 'Error while deleting the image',
      }
    );

    setLauncherThemeSession((themeData) => ({
      ...themeData,
      images: themeData.images.filter((item) => item.id !== img.id),
    }));
  };

  return (
    <>
      <Title>Assets</Title>

      {userSession.isLoggedIn && (
        <UploadItem $isDragOver={isDragOver}>
          <input
            type="file"
            size={1}
            accept="image/*"
            title=""
            onChange={(e) => onFileChange(e)}
            onDragEnter={() => setDragOver(true)}
            onDragLeave={() => setDragOver(false)}
            onMouseLeave={() => setDragOver(false)}
          />
          <span>{fileInputText}</span>
        </UploadItem>
      )}

      <Searchbar searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
      <Grid>
        <Item
          key={`asset-img-random`}
          onClick={() => onImgClick('https://picsum.photos/200/200')}
        >
          <img src={'https://picsum.photos/200/200'} />
          <span>Random image</span>
        </Item>

        {images?.length > 0 &&
          images
            ?.filter((img) => img.tag !== 'version_thumbnail')
            ?.filter((img) =>
              img.name
                ? img.name.toUpperCase().includes(searchQuery.toUpperCase())
                : true
            )
            .map((img) => (
              <Item key={`asset-img-${img.id}`}>
                <img src={img.url} onClick={() => onImgClick(img.url)} />
                {img.name && <span>{img.name}</span>}

                <Dialog
                  title="Delete image"
                  description="Are you sure you want to delete the image?"
                  triggerElement={
                    <div
                      className="removeButton"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <i className="icon-not-visible" />
                    </div>
                  }
                  cancelElement={<TextButton>Cancel</TextButton>}
                  actionElement={
                    <TextButton
                      handleClick={() => handleDeleteImage(img)}
                      type="error"
                    >
                      Yes, delete
                    </TextButton>
                  }
                >
                  <img width="100%" src={img.url} />
                </Dialog>
              </Item>
            ))}
      </Grid>
    </>
  );
};

const Title = styled.h1`
  position: sticky;
  top: 0;
  z-index: 1;

  background-color: var(--panel-bg-color-secondary);
`;

const UploadItem = styled.div<{ $isDragOver?: boolean }>`
  box-sizing: border-box;
  cursor: pointer;
  position: relative;
  width: 100%;
  height: 100px;
  margin-bottom: 10px;

  display: flex;
  justify-content: center;
  align-items: center;

  border: 1px dashed var(--text-color-secondary);
  border-radius: 10px;

  transition: border-color 0.3s ease;
  &:hover {
    border-color: var(--text-color);
  }

  & > input {
    position: absolute;
    width: 100%;
    height: 100%;
    font-size: 0;
    opacity: 0;
  }

  ${({ $isDragOver }) =>
    $isDragOver &&
    `
      background-color: var(--panel-bg-color-hover);
      border-color: var(--accent-color);
    `}
`;

const Grid = styled.div`
  display: flex;
  flex-wrap: wrap;
  padding-bottom: 5px;

  gap: 6px;
`;

const Item = styled.div`
  position: relative;
  width: calc(50% - 3px);

  outline: 1px solid transparent;
  border-radius: 10px;
  aspect-ratio: 1;
  overflow: hidden;

  display: flex;
  justify-content: center;
  align-items: center;

  & > img {
    cursor: pointer;
    user-select: none;
    max-height: 100%;
    object-fit: contain;
  }

  & > span {
    pointer-events: none;
    box-sizing: border-box;
    position: absolute;
    left: 0;
    bottom: 0;
    width: 100%;
    padding: 2px 5px;

    background-color: rgba(0, 0, 0, 0.7);
    color: #eee;
    text-align: center;
    font-size: 10px;

    overflow: hidden;
    text-overflow: ellipsis;
  }

  transition: outline-color 0.3s ease;

  &:hover {
    outline-color: var(--accent-color);
  }

  &:hover > .removeButton {
    opacity: 1;
  }
  .removeButton {
    transition:
      color 0.3s ease,
      opacity 0.3s ease;

    opacity: 0;
    background-color: var(--panel-bg-color);
    border-radius: 5px;

    position: absolute;
    top: 0;
    right: 0;
    padding: 5px;
    margin: 5px;

    &:hover {
      background-color: var(--panel-bg-color-hover);
    }
  }
`;
